// User settings — primarily for minting API tokens that power Siri Shortcuts
// and Tasker / Macrodroid flows. Plaintext token shown ONCE at mint; copy or
// lose it.

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';
import UserManager from '../components/UserManager.jsx';
import DistributionSettings from '../components/DistributionSettings.jsx';

export default function Settings() {
  const { isOwner } = useOutletContext();
  const [tokens, setTokens] = useState(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [justMinted, setJustMinted] = useState(null);   // { id, label, token } shown once
  const [copied, setCopied] = useState(false);

  async function load() {
    try { setTokens((await api.get('/api/auth/tokens')).tokens || []); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function mint(e) {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true); setErr(''); setCopied(false);
    try {
      const r = await api.post('/api/auth/tokens', { label: label.trim() });
      setJustMinted(r);
      setLabel('');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function revoke(t) {
    if (!confirm(`Revoke "${t.label}"? Any Siri/Tasker flow using it will stop working.`)) return;
    try { await api.delete(`/api/auth/tokens/${t.id}`); load(); }
    catch (e) { setErr(e.message); }
  }
  async function copy(text) {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch {}
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <header><h1 className="text-2xl font-serif text-olive-900">Settings</h1></header>

      {isOwner && <UserManager />}

      <section className="card space-y-3">
        <div>
          <h2 className="font-medium text-olive-900">API tokens</h2>
          <p className="text-sm text-ink-700/70">
            Mint one of these for each automation that calls KingStead — Siri Shortcuts on iPhone,
            Tasker on Android, etc. The token grants the same permissions as your account, so
            don't share it. See <code>docs/VOICE.md</code> for setup.
          </p>
        </div>

        <form onSubmit={mint} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1">
            <label className="label">Label (e.g. "siri-iphone", "tasker-pixel")</label>
            <input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="siri-iphone-jeff" />
          </div>
          <button className="btn-primary" disabled={busy || !label.trim()}>
            {busy ? 'Minting…' : 'Mint token'}
          </button>
        </form>

        {justMinted && (
          <div className="rounded-lg bg-gold-500/10 ring-1 ring-gold-500/40 p-3 space-y-2">
            <div className="font-medium text-ink-900">
              ✓ "{justMinted.label}" — copy this NOW; it won't be shown again:
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-cream-100 rounded px-3 py-2 text-sm break-all select-all">
                {justMinted.token}
              </code>
              <button type="button" onClick={() => copy(justMinted.token)} className="btn-ghost">
                {copied ? '✓ Copied' : 'Copy'}
              </button>
              <button type="button" onClick={() => setJustMinted(null)} className="text-sm text-ink-700/60 hover:text-rust-700">
                hide
              </button>
            </div>
            <p className="text-xs text-ink-700/70">
              Paste into your Siri Shortcut's Authorization header as <code>Bearer {justMinted.token.slice(0, 12)}…</code>
            </p>
          </div>
        )}

        {err && <div className="text-rust-700 text-sm">{err}</div>}

        {tokens === null ? (
          <p className="text-ink-700/60 italic">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="text-ink-700/60 italic">No tokens yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-ink-700/60 border-b border-olive-100">
                <th className="text-left py-2 px-2">Label</th>
                <th className="text-left py-2 px-2">Created</th>
                <th className="text-left py-2 px-2">Last used</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-right py-2 px-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.id} className="border-b border-olive-100/40">
                  <td className="py-1.5 px-2 font-medium">{t.label || <em>unnamed</em>}</td>
                  <td className="py-1.5 px-2 text-ink-700/70">{fmtDate(t.created_at)}</td>
                  <td className="py-1.5 px-2 text-ink-700/70">{t.last_used_at ? fmtDate(t.last_used_at) : <em>never</em>}</td>
                  <td className="py-1.5 px-2">
                    {t.revoked_at
                      ? <span className="pill bg-rust-500/10 text-rust-700">revoked</span>
                      : <span className="pill bg-olive-100 text-olive-900">active</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    {!t.revoked_at && (
                      <button onClick={() => revoke(t)} className="text-xs text-rust-700 hover:underline">Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card space-y-2">
        <h2 className="font-medium text-olive-900">Quick voice income</h2>
        <p className="text-sm text-ink-700/70">
          Open <a className="text-olive-700 hover:underline" href="/quick/income">/quick/income</a> for
          a one-tap voice → save flow. Works in any browser; pin to your home screen for the
          fastest path. Setup details: <code>VOICE.md</code>.
        </p>
      </section>

      <DistributionSettings />
    </div>
  );
}
